/**
 * Client-side mirror of the backend's CatalogConfig (services/Bids/reference/*_service.py).
 *
 * All seven admin screens, their forms, and the import screen are generated from this one table —
 * the same trick the backend uses to avoid seven hand-copied CRUD stacks. Adding a catalog means
 * adding an entry here, not a new page.
 *
 * `fields` must stay in sync with schemas/Bids/reference_schemas.py: a field named here that the
 * Create schema doesn't accept is rejected per-row on import and 400s on create.
 */

export type FieldType = 'text' | 'number' | 'date' | 'checkbox' | 'textarea' | 'json' | 'catalogRef';

export interface CatalogField {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  /** Show this field as a column in the list table (the key field is always shown). */
  inTable?: boolean;
  /** For `catalogRef`: the descriptor key whose active rows populate the dropdown. */
  refCatalog?: string;
  /** `number` only: the backend has no `ge=0` on this column, so allow negatives. */
  allowNegative?: boolean;
  /** `number` only: the backend column is an INTEGER — reject 2.5 client-side, not as a 400. */
  integer?: boolean;
}

export interface CatalogDescriptor {
  /** Tab id + URL query value. */
  key: string;
  label: string;
  /** Backend catalog route, e.g. '/equipment-catalog'. */
  url: string;
  /** `/reference-data/<kind>` slug. Absent = not importable (inventory: nullable natural key). */
  importKind?: string;
  /** The natural key — what an import upserts on and what confirm dialogs name. */
  keyField: string;
  keyLabel: string;
  fields: CatalogField[];
}

export const CATALOGS: CatalogDescriptor[] = [
  {
    key: 'equipment',
    label: 'Equipment',
    url: '/equipment-catalog',
    importKind: 'equipment',
    keyField: 'name',
    keyLabel: 'Name',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'category', label: 'Category', type: 'text', inTable: true },
      { name: 'unit', label: 'Unit', type: 'text', placeholder: '$/hr', inTable: true },
      { name: 'default_isr', label: 'Default ISR', type: 'number', inTable: true },
      { name: 'default_osr', label: 'Default OSR', type: 'number', inTable: true },
      // Internal cost beside the charge-out rates (Tier 4.3) — money, strips like them.
      { name: 'default_cost', label: 'Internal cost', type: 'number', inTable: true },
      { name: 'effective_from', label: 'Effective from', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    key: 'labour',
    label: 'Labour roles',
    url: '/labour-roles',
    importKind: 'labour',
    keyField: 'name',
    keyLabel: 'Name',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'default_rate', label: 'Default rate', type: 'number', inTable: true },
      // The MSA/force-account charge-out rate (0011). Blank = no FA rate agreed, and the server
      // REFUSES an FA line for the role rather than falling back to the bid rate.
      { name: 'default_fa_rate', label: 'FA rate (MSA)', type: 'number', inTable: true },
      { name: 'default_cost', label: 'Internal cost', type: 'number', inTable: true },
      { name: 'unit', label: 'Unit', type: 'text', placeholder: '$/hr', inTable: true },
      { name: 'effective_from', label: 'Effective from', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    key: 'cost-codes',
    label: 'Cost codes',
    url: '/cost-codes',
    importKind: 'cost-codes',
    keyField: 'code',
    keyLabel: 'Code',
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true, inTable: true },
      { name: 'unit_driver', label: 'Unit driver', type: 'text', inTable: true },
      { name: 'on_gantt', label: 'On Gantt', type: 'checkbox', inTable: true },
      { name: 'is_burn_rate', label: 'Burn rate', type: 'checkbox' },
      { name: 'is_force_account', label: 'Force account', type: 'checkbox' },
      { name: 'fa_descriptions', label: 'FA descriptions (JSON)', type: 'json' },
    ],
  },
  {
    key: 'phase-templates',
    label: 'Phase templates',
    url: '/phase-templates',
    importKind: 'phase-templates',
    keyField: 'name',
    keyLabel: 'Name',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'sequence', label: 'Sequence', type: 'number', integer: true, inTable: true },
      { name: 'default_production_rate', label: 'Production rate (m/day)', type: 'number', inTable: true },
      { name: 'unit_of_measure', label: 'Unit of measure', type: 'text', placeholder: 'm' },
      { name: 'default_crew', label: 'Default crew (JSON)', type: 'json' },
      { name: 'anchor_bucket_map', label: 'Anchor bucket map (JSON)', type: 'json' },
    ],
  },
  {
    key: 'config-defaults',
    label: 'Config defaults',
    url: '/bid-config-defaults',
    importKind: 'config-defaults',
    keyField: 'param_key',
    keyLabel: 'Parameter',
    fields: [
      { name: 'param_key', label: 'Parameter key', type: 'text', required: true },
      { name: 'param_value', label: 'Value (numeric)', type: 'number', allowNegative: true, inTable: true },
      { name: 'param_text', label: 'Value (text)', type: 'text', inTable: true },
      { name: 'unit', label: 'Unit', type: 'text', placeholder: '%', inTable: true },
      { name: 'effective_from', label: 'Effective from', type: 'date' },
    ],
  },
  {
    key: 'fluid-pipe-specs',
    label: 'Fluid / pipe specs',
    url: '/fluid-pipe-specs',
    importKind: 'fluid-pipe-specs',
    keyField: 'designation',
    keyLabel: 'Designation',
    fields: [
      { name: 'designation', label: 'Designation', type: 'text', required: true, placeholder: '10-5.56' },
      { name: 'od_mm', label: 'OD (mm)', type: 'number', inTable: true },
      { name: 'wall_mm', label: 'Wall (mm)', type: 'number', inTable: true },
      { name: 'm3_per_km', label: 'm³ / km', type: 'number', inTable: true },
      { name: 'm3_per_m', label: 'm³ / m', type: 'number' },
    ],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    url: '/inventory-items',
    // No importKind: asset_tag is nullable, so an upsert could not dedupe it (see
    // schemas/Bids/reference_import_schemas.py). Inventory is maintained here only.
    keyField: 'name',
    keyLabel: 'Name',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'category', label: 'Category', type: 'text', inTable: true },
      { name: 'asset_tag', label: 'Asset tag', type: 'text', inTable: true },
      // This specific unit's internal cost (Tier 4.3) — outranks the catalog default.
      { name: 'unit_cost', label: 'Unit cost', type: 'number', inTable: true },
      { name: 'quantity', label: 'Quantity', type: 'number', inTable: true },
      { name: 'equipment_item_id', label: 'Equipment item', type: 'catalogRef', refCatalog: 'equipment' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
];

export const catalogByKey = (key: string): CatalogDescriptor | undefined =>
  CATALOGS.find((c) => c.key === key);

/** Catalogs the JSON import supports, in tab order. */
export const IMPORTABLE = CATALOGS.filter((c) => c.importKind);
